import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { Slider, Stack, Typography, useTheme } from '@mui/material';
import { FixtureScorerRating } from '@/service/types';
import {
  ComparisonBox,
  NumberTextField,
} from '@/components/ComparisonRating/common';
import { calculateOverallScore } from '@/components/ComparisonRating/utils';

interface ComparisonRatingScoreProps {
  newRating: FixtureScorerRating | undefined;
  setNewRating: Dispatch<SetStateAction<FixtureScorerRating | undefined>>;
}

export const COMPARISON_RATING_QUANTITY_SCORE_INPUT_LABEL =
  'Quantity Score Input';
export const COMPARISON_RATING_QUANTITY_SCORE_SLIDER_LABEL =
  'Quantity Score Slider';
export const COMPARISON_RATING_LATENCY_SCORE_INPUT_LABEL =
  'Latency Score Input';
export const COMPARISON_RATING_LATENCY_SCORE_SLIDER_LABEL =
  'Latency Score Slider';

export const ComparisonRatingScore = ({
  newRating,
  setNewRating,
}: ComparisonRatingScoreProps) => {
  const theme = useTheme();
  const [quantityScore, setQuantityScore] = useState<number>(
    newRating?.latencyScore || 100
  );
  const [latencyScore, setLatencyScore] = useState<number>(
    newRating?.quantityScore || 100
  );

  useEffect(() => {
    setNewRating({
      ...newRating,
      quantityScore: quantityScore,
      latencyScore: latencyScore,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantityScore, latencyScore]);

  useEffect(() => {
    if (newRating) {
      setNewRating(calculateOverallScore(newRating));
    }
    if (
      newRating?.quantityScore !== undefined &&
      newRating?.latencyScore !== undefined
    ) {
      setLatencyScore(newRating.latencyScore);
      setQuantityScore(newRating.quantityScore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newRating?.quantityScore, newRating?.latencyScore]);

  const ratingInputHandler = (
    ratingType: 'latencyScore' | 'quantityScore',
    value: number
  ) => {
    if (value > 100 || value < 0) return;

    switch (ratingType) {
      case 'latencyScore':
        return setLatencyScore(value);
      case 'quantityScore':
        return setQuantityScore(value);
      default:
        return;
    }
  };

  return (
    <ComparisonBox gap={3}>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        Quantity Score
        <Slider
          aria-label={COMPARISON_RATING_QUANTITY_SCORE_SLIDER_LABEL}
          value={quantityScore}
          onChange={(event, newValue) => setQuantityScore(newValue as number)}
          sx={{ width: '50%' }}
        />
        <NumberTextField
          type='number'
          value={quantityScore}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            ratingInputHandler('quantityScore', Number(event.target.value))
          }
          key='quantity-score'
          InputProps={{
            inputProps: {
              max: 100,
              min: 0,
              'aria-label': COMPARISON_RATING_QUANTITY_SCORE_INPUT_LABEL,
            },
          }}
        />
      </Stack>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        Latency Score
        <Slider
          aria-label={COMPARISON_RATING_LATENCY_SCORE_SLIDER_LABEL}
          value={latencyScore}
          onChange={(event, newValue) => setLatencyScore(newValue as number)}
          sx={{ width: '50%' }}
        />
        <NumberTextField
          type='number'
          value={latencyScore}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            ratingInputHandler('latencyScore', Number(event.target.value))
          }
          key='latency-score'
          InputProps={{
            inputProps: {
              max: 100,
              min: 0,
              'aria-label': COMPARISON_RATING_LATENCY_SCORE_INPUT_LABEL,
            },
          }}
        />
      </Stack>
      <Stack flexDirection='row' gap={theme.spacing(0.5)}>
        <Typography>Overall Score:</Typography>
        <Typography fontWeight='bold'>{newRating?.overallScore}</Typography>
        <Typography>points</Typography>
        <Typography fontWeight='bold'>({newRating?.serviceLevel})</Typography>
      </Stack>
    </ComparisonBox>
  );
};
